import {Component, Input, OnInit} from '@angular/core';
import {FileTypes} from '@constants/file-types';
import {StatusEventSignalService} from '@services/administration/machine/status-event/status-event-signal.service';
import {
  StatusEventSignalValueParsingType
} from '@constants/machine/status-event-signal/status-event-signal-value-parsing-type.enum';
import {STATUS_EVENT_MAX_FILE_SIZE_ALLOWED} from '@constants/machine/status-event-signal/status-event-signal.constants';
import {NotificationService} from '@services/notification/notification.service';
import {TranslateService} from '@ngx-translate/core';
import {Signal} from '@models/dashboard/signal';
import {
  StatusEventSignalConfigurationPreviewComponent
} from '@components/administration/machine/machine-configuration/configuration/data-configuration/data-configuration-edit/status-event-signal-configuration/status-event-signal-configuration-preview/status-event-signal-configuration-preview.component';
import {StatusEventSignalEventsDetails} from '@models/machine/status-event-signal/status-event-signal-events-details';
import {StatusEventDetails} from '@models/machine/status-event-signal/status-event-details';
import {
  StatusEventSignalParsingException
} from '@models/machine/status-event-signal/status-event-signal-parsing-exception';
import {ModalService} from '@services/modal/modal.service';

@Component({
    selector: 'scitis-status-event-signal-configuration',
    templateUrl: './status-event-signal-configuration.component.html',
    styleUrls: ['./status-event-signal-configuration.component.scss'],
    standalone: false
})
export class StatusEventSignalConfigurationComponent implements OnInit {

  @Input()
  signal: Signal = new Signal('', '', '', '');

  @Input()
  public isLoadingDetails: boolean = false;

  @Input()
  public isHigherThanAdmin: boolean = false;

  public errorParsingCSV: boolean = false;

  public parsingTypes: StatusEventSignalValueParsingType[] = [StatusEventSignalValueParsingType.BIT, StatusEventSignalValueParsingType.VALUE];

  public outcomeOfParsingCSV: string = '';

  public uploadControlConfiguration = {
    title: this.translate.instant('Upload CSV'),
    message: this.translate.instant('Uploading CSV'),
    label: this.translate.instant('Upload'),
    fileType: FileTypes.CSV,
    maxUploadFileSize: STATUS_EVENT_MAX_FILE_SIZE_ALLOWED
  };

  public downloadControlConfiguration = {
    title: this.translate.instant('Download CSV'),
    label: this.translate.instant('Download'),
    fileType: FileTypes.CSV,
    fileName: '',
    details: ''
  };

  public previewControlConfiguration = {
    title: this.translate.instant('Preview CSV'),
    label: this.translate.instant('Preview'),
  };

  public deleteControlConfiguration = {
    title: this.translate.instant('Remove CSV'),
    label: this.translate.instant('Remove'),
  };


  constructor(
      private notification: NotificationService,
      private translate: TranslateService,
      private modalService: ModalService,
      private statusEventSignalService: StatusEventSignalService) {

  }

  ngOnInit(): void {
    if (!this.signal.statusEventsDetails) {
      this.signal.statusEventsDetails = new StatusEventSignalEventsDetails(this.signal.id,
          this.signal.name, this.signal.label, this.signal.unit, StatusEventSignalValueParsingType.BIT,
          new Map<string, StatusEventDetails>(), this.signal.parentId);
    }
  }

  onFileUploadComplete(content: string | ArrayBuffer | null) {
    this.outcomeOfParsingCSV = '';
    try {
      this.signal.statusEventsDetails.definitions = this.statusEventSignalService.importCsv(this.signal.statusEventsDetails.parsingType, String(content));
      this.outcomeOfParsingCSV = this.translate.instant('CSV file content parsed with success') + '....';
      this.notification.success(this.outcomeOfParsingCSV, '', true);
      this.errorParsingCSV = false;
    } catch (e) {
      const error: StatusEventSignalParsingException = e as StatusEventSignalParsingException;
      this.outcomeOfParsingCSV = error.message;
      this.signal.statusEventsDetails.definitions = new Map<string, StatusEventDetails>();
      this.notification.error('Error uploading / parsing CSV file', error, '', true);
      this.errorParsingCSV = true;
    }
  }

  onChangeCsvMappingDisabledFlag() {
    const mappedByCsv = this.signal.statusEventsDetails.csvMappingDisabled;
    this.signal.statusEventsDetails = new StatusEventSignalEventsDetails(this.signal.id,
        this.signal.name, this.signal.label, this.signal.unit, StatusEventSignalValueParsingType.BIT,
        new Map<string, StatusEventDetails>(), this.signal.parentId);
    this.signal.statusEventsDetails.csvMappingDisabled = mappedByCsv;
    this.outcomeOfParsingCSV = '';
  }

  onFileUploadError(error: ProgressEvent<FileReader>) {
    this.outcomeOfParsingCSV = this.translate.instant('There was and error uploading CSV file') + '....';
    this.notification.error('Error uploading file', error, 'ErrorSignal', true);
  }

  onFileDownloadRequested($event: boolean) {
    if ($event) {
      this.downloadControlConfiguration.fileName = this.generateExportFileName();
      this.downloadControlConfiguration.details = this.statusEventSignalService.exportCsv(this.signal.statusEventsDetails.definitions);
    }
  }

  public generateExportFileName(): string {
    return this.statusEventSignalService.generateExportFileName(this.signal.name);
  }

  onShowPreview() {
    let htmlDetails = '';
    try {
      htmlDetails = this.statusEventSignalService.generateHtml(this.signal.statusEventsDetails.definitions);
    } catch (e) {
      const error: StatusEventSignalParsingException = e as StatusEventSignalParsingException;
      this.outcomeOfParsingCSV = this.translate.instant('There was and error parsing error event details to generate preview') + '....';
      this.notification.error('Error parsing error details to generate preview', error, '', true);
      return;
    }

    const modalRef = this.modalService.open(StatusEventSignalConfigurationPreviewComponent, {size: 'xl'});
    modalRef.componentInstance.htmlDetails = htmlDetails;

  }

  onParsingTypeChange() {
    this.outcomeOfParsingCSV = '';
    try {

      this.statusEventSignalService.validateStatusEventDefinitions(this.signal.statusEventsDetails.parsingType, this.signal.statusEventsDetails.definitions);
      this.errorParsingCSV = false;
    } catch (e) {
      const error: StatusEventSignalParsingException = e as StatusEventSignalParsingException;
      this.outcomeOfParsingCSV = error.message;
      this.signal.statusEventsDetails.definitions = new Map<string, StatusEventDetails>();
      this.notification.error('The event definitions are not valid fro current parsing type', error, '', true);
      this.errorParsingCSV = true;
    }
  }

  public onRemoveConfiguration() {
    this.signal.statusEventsDetails.definitions = new Map<string, StatusEventDetails>();
  }

  public shouldDisableButton() {
    return this.isLoadingDetails || this.errorParsingCSV || this.signal.statusEventsDetails.definitions.size === 0;
  }
}
